import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Undo2, Redo2 } from "lucide-react";
import { useSiteProfile } from "./hooks/useSiteProfile";
import { FINANCIAL_DEFAULTS, SCENARIO_PRESETS, type FinancialParams, type ChargerConfig, type CustomOpexItem, type CustomCapexItem } from "./utils/financialDefaults";
import { useFinancialModel } from "./hooks/useFinancialModel";
import FinancialDashboard from "./financial/FinancialDashboard";

import type { SimulationResults, SystemConfig } from "./utils/financialCalculations";
import { exportToExcel } from "./utils/excelExport";
import { generatePDF } from "./utils/pdfExport";

const MODEL_VERSION = "2.4";
const MD = [31,28,31,30,31,30,31,31,30,31,30,31];
const MAX_HISTORY = 50;

interface BlokstroomConfig {
  enabled: boolean;
  power: number;   // MW
  start: number;    // hour
  end: number;      // hour
}

function isBlokstroomHour(h: number, blok: BlokstroomConfig): boolean {
  if (!blok.enabled) return false;
  return blok.start < blok.end ? (h >= blok.start && h < blok.end) : (h >= blok.start || h < blok.end);
}

function runSimulation(SP: number[][], solarRef: number, pv: number, bE: number, bP: number, eff: number, dod: number, buP: number, gMax: number, dem: number, deg: number, projYear: number, chp: number[], CSF: number[], blok: BlokstroomConfig) {
  const effectiveBE = bE * Math.pow(1 - deg / 100, projYear - 1);
  const sqEff = Math.sqrt(eff / 100);
  const usable = effectiveBE * (dod / 100);
  const scale = pv / solarRef;
  let soc = usable * 0.5;
  // `tUnmet` telt de vraag die net, batterij én noodaggregaat samen niet konden
  // dekken. Deze lus liet dat tekort tot nu toe vallen, terwijl `tDem` de volle
  // vraag bleef optellen — de businesscase factureerde dus ook het niet-geleverde
  // deel. Op SL 18-3 (Wognum, pv 2 MWp, 20 MWh/10 MW BESS, aansluiting 0,33 MW,
  // geen aggregaat, 19.000 MWh vraag, jaar 15) is dat 14.844,4 van de 18.994,3 MWh
  // (78,2 %); er blijft 4.149,9 MWh over die echt verkocht kan worden.
  // Drempel 0,0001 MWh is dezelfde als in HubSimulator.tsx en simulationEngine.ts.
  let tPV = 0, tDem = 0, tDirect = 0, tBIn = 0, tBOut = 0, tGI = 0, tGE = 0, tBU = 0, tCurt = 0, tBlokstroomImport = 0, tUnmet = 0;

  for (let m = 0; m < 12; m++) {
    for (let d = 0; d < MD[m]; d++) {
      for (let h = 0; h < 24; h++) {
        const pvH = SP[m][h] * scale / 1000;
        const demH = (dem * chp[h] * CSF[m]) / (24 * 365.25 * (chp.reduce((a, b) => a + b, 0) / 24) * (CSF.reduce((a, b) => a + b, 0) / 12));
        const net = pvH - demH;
        const effectiveGMax = isBlokstroomHour(h, blok) ? gMax + blok.power : gMax;

        if (net >= 0) {
          tDirect += demH; tDem += demH; tPV += pvH;
          let surplus = net;
          const canCharge = Math.min(surplus, bP, (usable - soc) / sqEff);
          if (canCharge > 0) { tBIn += canCharge; surplus -= canCharge; soc = Math.min(usable, soc + canCharge * sqEff); }
          const canExport = Math.min(surplus, gMax);
          if (canExport > 0) { tGE += canExport; surplus -= canExport; }
          if (surplus > 0) tCurt += surplus;
        } else {
          tDirect += pvH; tDem += demH; tPV += pvH;
          let deficit = -net;
          const rawDischarge = Math.min(deficit / sqEff, bP, soc);
          const canDischarge = rawDischarge * sqEff;
          if (canDischarge > 0) { tBOut += canDischarge; deficit -= canDischarge; soc = Math.max(0, soc - rawDischarge); }
          const canImport = Math.min(deficit, effectiveGMax);
          if (canImport > 0) {
            tGI += canImport; deficit -= canImport;
            if (isBlokstroomHour(h, blok)) {
              const baseImport = Math.min(canImport, gMax);
              tBlokstroomImport += canImport - baseImport;
            }
          }
          const canBU = Math.min(deficit, buP);
          if (canBU > 0) { tBU += canBU; deficit -= canBU; }
          if (deficit > 0.0001) tUnmet += deficit;
        }
        // Proactief blokstroom laden: batterij opladen tijdens blokstroom-uren (los van gMax)
        if (isBlokstroomHour(h, blok) && soc < usable) {
          const chargeRoom = (usable - soc) / sqEff;
          const chargePowerLeft = Math.max(0, bP - (net >= 0 ? Math.min(net, bP, (usable - soc) / sqEff) : 0));
          const blokCharge = Math.min(blok.power, chargePowerLeft, chargeRoom);
          if (blokCharge > 0) {
            tBIn += blokCharge; tGI += blokCharge; soc += blokCharge * sqEff;
            tBlokstroomImport += blokCharge;
          }
        }

        soc = Math.max(0, Math.min(usable, soc));
      }
    }
  }

  return {
    pvProduction: tPV, totalDelivered: tDem, gridImport: tGI,
    gridExport: tGE, backupUsed: tBU, curtailment: tCurt, bLoss: (tBIn - tBOut),
    blokstroomImport: tBlokstroomImport,
    // Zonder dit veld levert `deliveredEnergy()` NaN en toont de hele
    // Businesscase-tab €NaN. Met het veld rekent hij over de werkelijke levering.
    unmet: tUnmet,
  };
}

interface HubBusinessCaseProps {
  initialFinancialParams?: Record<string, any>;
  onFinancialParamsChange?: (params: Record<string, any>) => void;
}

export default function HubBusinessCase({ initialFinancialParams, onFinancialParamsChange }: HubBusinessCaseProps) {
  const { profile } = useSiteProfile();
  

  // Load technical params from simulation state if available
  const simState = (() => {
    try {
      const raw = sessionStorage.getItem(`sim_state_${profile.id}`);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();
  const rawVals = simState ?? profile.defaults;
  const pv = rawVals.pv, bE = rawVals.bE, bP = rawVals.bP, eff = rawVals.eff, dod = rawVals.dod, buP = rawVals.buP, dem = rawVals.dem;
  // BUG 3 fix: migrate legacy kW values to MW
  const gMax = rawVals.gMax > 50 ? rawVals.gMax / 1000 : rawVals.gMax;
  // Blokstroom config from sim state
  const blokConfig: BlokstroomConfig = {
    enabled: simState?.blokstroomEnabled ?? false,
    power: simState?.blokstroomPower ?? 2,
    start: simState?.blokstroomStart ?? 0,
    end: simState?.blokstroomEnd ?? 6,
  };

  // Session persistence for business case
  const bcStorageKey = `bc_state_${profile.id}`;
  const loadBcState = () => {
    try {
      const raw = sessionStorage.getItem(bcStorageKey);
      if (raw) return JSON.parse(raw);
    } catch {}
    return null;
  };
  const savedBc = loadBcState();

  const [deg, setDeg] = useState(initialFinancialParams?.deg ?? savedBc?.deg ?? profile.defaults.deg);
  const [chargingProfile, setChargingProfile] = useState<number[]>(() => [...profile.chargingProfile]);
  const [financialParams, setFinancialParams] = useState<FinancialParams>(() => {
    if (initialFinancialParams?.financialParams) return { ...FINANCIAL_DEFAULTS, ...initialFinancialParams.financialParams };
    return savedBc?.financialParams ?? { ...FINANCIAL_DEFAULTS };
  });
  const [fiscalEnabled, setFiscalEnabled] = useState(initialFinancialParams?.fiscalEnabled ?? savedBc?.fiscalEnabled ?? false);

  // Report financial params changes to parent
  useEffect(() => {
    onFinancialParamsChange?.({ financialParams, deg, fiscalEnabled });
  }, [financialParams, deg, fiscalEnabled]);

  useEffect(() => {
    sessionStorage.setItem(bcStorageKey, JSON.stringify({ financialParams, deg, fiscalEnabled }));
  }, [financialParams, deg, fiscalEnabled, bcStorageKey]);

  const prevProfileRef = useRef(profile.id);
  useEffect(() => {
    if (prevProfileRef.current !== profile.id) {
      prevProfileRef.current = profile.id;
      sessionStorage.removeItem(bcStorageKey);
      setDeg(profile.defaults.deg);
      setChargingProfile([...profile.chargingProfile]);
      setFinancialParams({ ...FINANCIAL_DEFAULTS });
      setFiscalEnabled(false);
    }
  }, [profile.id]);

  const numTrucks = useMemo(() => profile.fleet.reduce((s, f) => s + f.n, 0), [profile]);

  // Undo/Redo
  const [past, setPast] = useState<FinancialParams[]>([]);
  const [future, setFuture] = useState<FinancialParams[]>([]);
  const [lockedParams, setLockedParams] = useState<Set<keyof FinancialParams>>(new Set());
  const [bessLinked, setBessLinked] = useState(false);

  const sim: SimulationResults = useMemo(() =>
    runSimulation(profile.solarProfile, profile.solarRefCapacity, pv, bE, bP, eff, dod, buP, gMax, dem, deg, 1, chargingProfile, profile.seasonalFactors, blokConfig),
    [profile, deg, chargingProfile, pv, bE, bP, eff, dod, buP, gMax, dem, blokConfig]
  );

  const sys: SystemConfig = useMemo(() => ({
    pvCapacity: pv, bessEnergy: bE, bessPower: bP, backupPower: buP, gridMax: gMax, numTrucks,
  }), [pv, bE, bP, buP, gMax, numTrucks]);

  const model = useFinancialModel(sim, sys, financialParams, deg, fiscalEnabled);

  const pushUndo = useCallback((current: FinancialParams) => {
    setPast(prev => [...prev.slice(-(MAX_HISTORY - 1)), current]);
    setFuture([]);
  }, []);

  const handleParamChange = useCallback((key: keyof FinancialParams, value: number) => {
    setFinancialParams(prev => {
      pushUndo(prev);
      return { ...prev, [key]: value };
    });
  }, [pushUndo]);

  const handleChargerDetailChange = useCallback((config: ChargerConfig[] | undefined) => {
    setFinancialParams(prev => {
      pushUndo(prev);
      return { ...prev, chargerDetailConfig: config };
    });
  }, [pushUndo]);

  const handleCustomOpexChange = useCallback((items: CustomOpexItem[]) => {
    setFinancialParams(prev => {
      pushUndo(prev);
      return { ...prev, customOpexItems: items };
    });
  }, [pushUndo]);

  const handleCustomCapexChange = useCallback((items: CustomCapexItem[]) => {
    setFinancialParams(prev => {
      pushUndo(prev);
      return { ...prev, customCapexItems: items };
    });
  }, [pushUndo]);

  const handleLoadPreset = useCallback((preset: Partial<FinancialParams>) => {
    setFinancialParams(prev => {
      pushUndo(prev);
      const filtered = { ...prev, ...preset };
      lockedParams.forEach(key => {
        (filtered as any)[key] = prev[key];
      });
      return filtered;
    });
  }, [pushUndo, lockedParams]);

  const undo = useCallback(() => {
    if (past.length === 0) return;
    setFuture(prev => [financialParams, ...prev.slice(0, MAX_HISTORY - 1)]);
    const p = [...past];
    const last = p.pop()!;
    setPast(p);
    setFinancialParams(last);
  }, [past, financialParams]);

  const redo = useCallback(() => {
    if (future.length === 0) return;
    setPast(prev => [...prev.slice(-(MAX_HISTORY - 1)), financialParams]);
    const fut = [...future];
    const next = fut.shift()!;
    setFuture(fut);
    setFinancialParams(next);
  }, [future, financialParams]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo]);

  const toggleLock = useCallback((key: keyof FinancialParams) => {
    setLockedParams(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleFleetImport = useCallback((p: number[]) => {
    setChargingProfile(p);
  }, []);

  const handleExcelExport = () => {
    exportToExcel(model.kpis, model.cashflows, model.capex, model.sensitivity, financialParams);
  };

  const [printMode, setPrintMode] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const handlePdfExport = async () => {
    setPdfLoading(true);
    setPrintMode(true);
    await new Promise(r => setTimeout(r, 1500));
    try {
      await generatePDF('bc-pdf-export', `BusinessCase_${profile.name}_v${MODEL_VERSION}.pdf`, false, `Business Case ${profile.name} — Model v${MODEL_VERSION}`);
    } finally {
      setPrintMode(false);
      setPdfLoading(false);
    }
  };

  return (
    <div className="text-foreground">
      {pdfLoading && (
        <div className="fixed inset-0 z-[9999] bg-background/80 flex flex-col items-center justify-center gap-4">
          <div className="w-12 h-12 border-4 border-muted-foreground/30 border-t-foreground rounded-full animate-spin" />
          <div className="text-foreground font-semibold text-sm">PDF wordt gegenereerd…</div>
          <div className="text-muted-foreground text-xs">Even geduld, alle secties worden vastgelegd</div>
        </div>
      )}
      <div className={`${printMode ? 'w-[794px] bg-white' : ''}`} id="bc-pdf-export">
        {/* PDF Cover */}
        {printMode && (
          <div data-pdf-section="cover" className="bg-white rounded-xl border border-border shadow-sm mb-6" style={{ minHeight: 700 }}>
            <div className="text-center px-8 pt-12 pb-8">
              <div className="text-3xl font-extrabold text-foreground mb-2">Business Case Rapport</div>
              <div className="text-xl font-semibold text-muted-foreground mb-1">{profile.name}</div>
              <div className="text-sm text-muted-foreground">
                {new Date().toLocaleDateString('nl-NL', { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
              <div className="text-xs text-muted-foreground mt-1 font-mono">Model v{MODEL_VERSION}</div>
            </div>
            <div className="mx-8 mb-6 p-4 bg-muted rounded-lg border border-border">
              <div className="text-xs font-bold text-foreground mb-2">⚙️ Systeemconfiguratie</div>
              <div className="grid grid-cols-3 gap-2 text-[11px]">
                <div><span className="text-muted-foreground">PV:</span> <strong>{pv} MWp</strong></div>
                <div><span className="text-muted-foreground">BESS:</span> <strong>{bE} MWh / {bP} MW</strong></div>
                <div><span className="text-muted-foreground">Backup:</span> <strong>{buP} MW</strong></div>
                <div><span className="text-muted-foreground">Grid:</span> <strong>{gMax} MW</strong></div>
                <div><span className="text-muted-foreground">Vraag:</span> <strong>{dem} MWh</strong></div>
                <div><span className="text-muted-foreground">Trucks:</span> <strong>{numTrucks}</strong></div>
              </div>
            </div>
            <div className="mx-8 pt-4 pb-6 border-t border-border text-[9px] text-muted-foreground">
              Indicatief model. Niet geschikt voor besluitvorming zonder professionele validatie.
            </div>
          </div>
        )}

        {/* Config bar */}
        <div className="mb-3 px-4 py-2 bg-card rounded-lg border border-border shadow-sm flex flex-wrap items-center gap-x-5 gap-y-1 text-[10px]" data-pdf-section="config">
          <span className="font-bold text-foreground">⚙️</span>
          <span><span className="text-muted-foreground">PV</span> <strong>{pv} MWp</strong></span>
          <span><span className="text-muted-foreground">BESS</span> <strong>{bE} MWh / {bP} MW</strong></span>
          <span><span className="text-muted-foreground">Backup</span> <strong>{buP} MW</strong></span>
          <span><span className="text-muted-foreground">Grid</span> <strong>{gMax} MW</strong></span>
          <span><span className="text-muted-foreground">Vraag</span> <strong>{dem} MWh</strong></span>
          <span><span className="text-muted-foreground">Trucks</span> <strong>{numTrucks}</strong></span>
          <span><span className="text-muted-foreground">Eff</span> <strong>{eff}%</strong></span>
          <span><span className="text-muted-foreground">DoD</span> <strong>{dod}%</strong></span>
          <span className="ml-auto text-[9px] text-muted-foreground font-mono">Model v{MODEL_VERSION}</span>
        </div>

        {/* Toolbar */}
        {!printMode && (
          <div className="mb-4 px-4 py-2.5 bg-card rounded-xl border border-border shadow-sm flex items-center gap-2 flex-wrap print:hidden">
            <button onClick={undo} disabled={past.length === 0} title={`Ongedaan maken (${past.length})`}
              className="p-1.5 rounded-md border border-border bg-muted text-foreground hover:bg-accent cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              <Undo2 size={14} />
            </button>
            <button onClick={redo} disabled={future.length === 0} title={`Opnieuw (${future.length})`}
              className="p-1.5 rounded-md border border-border bg-muted text-foreground hover:bg-accent cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              <Redo2 size={14} />
            </button>

            <div className="w-px h-5 bg-border mx-1" />

            {SCENARIO_PRESETS.map(preset => (
              <button key={preset.name}
                onClick={() => {
                  const filtered = { ...FINANCIAL_DEFAULTS, ...preset.params };
                  lockedParams.forEach(key => {
                    (filtered as any)[key] = financialParams[key];
                  });
                  handleLoadPreset(filtered);
                }}
                className="px-2.5 py-1 text-[10px] font-semibold rounded-lg border border-border bg-muted text-foreground hover:bg-accent cursor-pointer transition-colors"
                title={preset.description}>
                {preset.icon} {preset.name}
              </button>
            ))}

            <div className="flex-1" />

            <button onClick={handlePdfExport}
              className="px-3 py-1 text-[10px] font-semibold rounded-lg border border-border bg-muted text-foreground hover:bg-accent cursor-pointer transition-colors">
              📄 PDF
            </button>
            <button onClick={handleExcelExport}
              className="px-3 py-1 text-[10px] font-semibold rounded-lg border border-border bg-muted text-foreground hover:bg-accent cursor-pointer transition-colors">
              📊 Excel
            </button>
          </div>
        )}

        <FinancialDashboard
          params={financialParams}
          onParamChange={handleParamChange}
          onChargerDetailChange={handleChargerDetailChange}
          onCustomOpexChange={handleCustomOpexChange}
          onCustomCapexChange={handleCustomCapexChange}
          model={model}
          sys={sys}
          sim={sim}
          degradation={deg}
          lockedParams={lockedParams}
          onToggleLock={toggleLock}
          bessLinked={bessLinked}
          onToggleBessLink={() => setBessLinked(b => !b)}
          onFleetImport={handleFleetImport}
          fiscalEnabled={fiscalEnabled}
          onToggleFiscal={() => setFiscalEnabled(f => !f)}
          profileDefaults={profile.defaults}
          printMode={printMode}
          onResetToDefaults={() => {
            sessionStorage.removeItem(`sim_state_${profile.id}`);
            window.location.reload();
          }}
        />

        <footer className="mt-5 py-3 border-t border-border flex justify-between items-center flex-wrap gap-2">
          <div className="text-[9px] text-muted-foreground">© 2026 — Business Case {profile.version} · Model v{MODEL_VERSION}</div>
          <div className="text-[9px] text-muted-foreground">Indicatief model. Niet geschikt voor besluitvorming zonder professionele validatie.</div>
        </footer>
      </div>
    </div>
  );
}
